import { useState, useEffect, useRef, useCallback } from "react";
import { pdf } from "@react-pdf/renderer";
import { REINFORCEMENT_CATEGORY } from "../constants";
import { fetchManifest, fetchFactionById } from "../lib/dataApi";
import { findUnit, isAtMax, computeTotals, currentLimit, findReinforcementUnit, computeUnlocked, pruneLocked } from "../lib/army";
import RosterPdfDocument from "../components/RosterPdfDocument";

let _iidSeq = 0;
const newInstanceId = () => `i${Date.now().toString(36)}${(_iidSeq++).toString(36)}`;
const unitTotalQty = (roster, unitId) =>
  roster.reduce((s, e) => s + (e.unitId === unitId ? e.qty : 0), 0);

async function fetchFaction(src) {
  const data = await fetchFactionById(src.id);
  if (!Array.isArray(data.units)) throw new Error("Malformed army file (missing 'units' array)");
  data._sourceId = src.id;
  return data;
}

export default function useArmyBuilder(user) {
  const [sources, setSources] = useState([]);
  const [faction, setFaction] = useState(null);
  const [roster, setRoster] = useState([]);
  const [filterCategory, setFilterCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [pointsMode, setPointsMode] = useState("750");
  const [customLimit, setCustomLimit] = useState(750);
  const [selectedFactionId, setSelectedFactionId] = useState("");
  const [banner, setBanner] = useState({ msg: "", isError: false, show: false });
  const [factionMeta, setFactionMeta] = useState("Loading army data\u2026");
  const [showReinforcements, setShowReinforcements] = useState(false);
  const bannerTimer = useRef();

  const showBanner = useCallback((msg, isError = false) => {
    setBanner({ msg, isError, show: true });
    clearTimeout(bannerTimer.current);
    bannerTimer.current = setTimeout(() => setBanner((b) => ({ ...b, show: false })), 5000);
  }, []);

  const loadFactionById = useCallback(async (id, keepRoster, srcList) => {
    const list = srcList || sources;
    const src = list.find((s) => s.id === id);
    if (!src) return;
    setFactionMeta("Loading " + src.name + "\u2026");
    try {
      const data = await fetchFaction(src);
      setFaction(data);
      if (!keepRoster) {
        setRoster([]);
        setShowReinforcements(false);
      }
      setFilterCategory("All");
      setSearchTerm("");
    } catch (err) {
      setFaction(null);
      setFactionMeta("Could not load this army file.");
      showBanner("Failed to load army data: " + err.message + ". If this is a GitHub URL, make sure it's the raw.githubusercontent.com link and CORS isn't blocked.", true);
    }
  }, [sources, showBanner]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const data = await fetchManifest();
        const list = (data.armyList || []).map((e) => ({
          id: e.id, name: e.name, supplement: e.supplement, year: e.year, url: e.url,
        }));
        if (!list.length) throw new Error("Empty army list");
        setSources(list);
        setSelectedFactionId(list[0].id);
        loadFactionById(list[0].id, false, list);
      } catch (err) {
        setFaction(null);
        setFactionMeta("Could not load army data.");
        showBanner("Could not load army data: " + err.message, true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const currentQtyOf = useCallback((unitId) => {
    return roster.reduce((s, x) => s + (x.unitId === unitId ? x.qty : 0), 0);
  }, [roster]);

  const onFactionChange = useCallback((id) => {
    if (roster.length && !window.confirm("Switching army will clear your current roster. Continue?")) {
      return;
    }
    setSelectedFactionId(id);
    loadFactionById(id, false);
  }, [roster.length, loadFactionById]);

  const addUnit = useCallback((unitId) => {
    setRoster((prev) => {
      const unit = findUnit(faction, unitId);
      const total = unitTotalQty(prev, unitId);
      // Button becomes inactive once the total quantity across all cards is at max.
      if (unit && isAtMax(unit, total)) return prev;
      const hasOptions = unit && Array.isArray(unit.options) && unit.options.length > 0;
      if (hasOptions) {
        // Units with options get a fresh, independently-configured card each add.
        return [...prev, { instanceId: newInstanceId(), unitId, qty: 1, upgrades: [], optionalSpecialRules: [], options: [] }];
      }
      // Units without options keep the single-card, incrementing-qty behaviour.
      const existing = prev.find((e) => e.unitId === unitId);
      if (existing) {
        return prev.map((e) => (e.unitId === unitId ? { ...e, qty: e.qty + 1 } : e));
      }
      let startQty = 1;
      if (unit && unit.minQty != null) startQty = Math.max(1, unit.minQty);
      if (unit && unit.maxQty != null) startQty = Math.min(startQty, unit.maxQty);
      return [...prev, { instanceId: newInstanceId(), unitId, qty: startQty, upgrades: [], optionalSpecialRules: [], options: [] }];
    });
  }, [faction]);

  const changeQty = useCallback((instanceId, delta) => {
    setRoster((prev) =>
      pruneLocked(
        faction,
        prev
          .map((e) => {
            if (e.instanceId !== instanceId) return e;
            const unit = findUnit(faction, e.unitId);
            let newQty = e.qty + delta;
            if (delta > 0 && unit && unit.maxQty != null) {
              // Cap so the summed qty across all cards for this unit never exceeds max.
              const others = unitTotalQty(prev, e.unitId) - e.qty;
              newQty = Math.min(newQty, unit.maxQty - others);
            }
            return { ...e, qty: newQty };
          })
          .filter((e) => e.qty > 0)
      )
    );
  }, [faction]);

  const removeUnit = useCallback((instanceId) => {
    setRoster((prev) => pruneLocked(faction, prev.filter((e) => e.instanceId !== instanceId)));
  }, [faction]);

  const moveUnit = useCallback((instanceId, direction) => {
    setRoster((prev) => {
      const idx = prev.findIndex((e) => e.instanceId === instanceId);
      if (idx < 0) return prev;
      const unit = findUnit(faction, prev[idx].unitId);
      if (!unit) return prev;
      const cat = unit.category;
      let swapIdx = -1;
      if (direction < 0) {
        for (let i = idx - 1; i >= 0; i--) {
          const u = findUnit(faction, prev[i].unitId);
          if (u && u.category === cat) { swapIdx = i; break; }
        }
      } else {
        for (let i = idx + 1; i < prev.length; i++) {
          const u = findUnit(faction, prev[i].unitId);
          if (u && u.category === cat) { swapIdx = i; break; }
        }
      }
      if (swapIdx < 0) return prev;
      const next = prev.slice();
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    });
  }, [faction]);

  const toggleGroupItem = useCallback((instanceId, groupKey, itemId) => {
    setRoster((prev) =>
      prev.map((e) => {
        if (e.instanceId !== instanceId) return e;
        const arr = e[groupKey] ? e[groupKey].slice() : [];
        const idx = arr.indexOf(itemId);
        if (idx >= 0) arr.splice(idx, 1);
        else arr.push(itemId);
        return { ...e, [groupKey]: arr };
      })
    );
  }, []);

  const toggleReinforcements = useCallback((checked) => {
    setShowReinforcements(checked);
    if (checked) {
      const unit = findReinforcementUnit(faction);
      if (unit) {
        setRoster((prev) => {
          if (prev.find((e) => e.unitId === unit.id)) return prev;
          let startQty = 1;
          if (unit.minQty != null) startQty = Math.max(1, unit.minQty);
          if (unit.maxQty != null) startQty = Math.min(startQty, unit.maxQty);
          return [...prev, { instanceId: newInstanceId(), unitId: unit.id, qty: startQty, upgrades: [], optionalSpecialRules: [], options: [] }];
        });
      }
    } else {
      setFilterCategory((fc) => (fc === REINFORCEMENT_CATEGORY ? "All" : fc));
      setRoster((prev) =>
        prev.filter((e) => {
          const u = findUnit(faction, e.unitId);
          return !(u && u.category === REINFORCEMENT_CATEGORY);
        })
      );
    }
  }, [faction]);

  const saveRoster = useCallback(() => {
    if (!faction) {
      showBanner("Load an army before saving.", true);
      return;
    }
    const defaultName = `${faction.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${currentLimit(pointsMode, customLimit)}pts`;
    const entered = window.prompt("Enter a filename to save this army list:", defaultName);
    if (entered === null) return;
    const fileName = (entered.trim() || defaultName).replace(/\.json$/i, "").replace(/[^\w-]+/g, "-");
    const payload = {
      _type: "northag-centag-army-list",
      _version: 1,
      factionId: faction._sourceId,
      factionName: faction.name,
      factionSourceUrl: sources.find((s) => s.id === faction._sourceId)?.url || null,
      pointsMode,
      customLimit,
      roster,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showBanner("Army list saved.", false);
  }, [faction, sources, pointsMode, customLimit, roster, showBanner]);

  const loadRosterFromFile = useCallback(async (file) => {
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      if (payload._type !== "northag-centag-army-list") throw new Error("Not a recognised army list file");

      let srcList = sources;
      if (payload.factionSourceUrl && !sources.find((s) => s.id === payload.factionId)) {
        srcList = [...sources, { id: payload.factionId, name: payload.factionName, url: payload.factionSourceUrl }];
        setSources(srcList);
      }
      const src = srcList.find((s) => s.id === payload.factionId);
      setSelectedFactionId(payload.factionId);
      const data = await fetchFaction(src);
      setFaction(data);

      setPointsMode(payload.pointsMode || "750");
      setCustomLimit(payload.customLimit || 750);

      const newRoster = (payload.roster || [])
        .filter((e) => findUnit(data, e.unitId))
        .map((e) => ({
          instanceId: e.instanceId || newInstanceId(),
          unitId: e.unitId,
          qty: e.qty,
          upgrades: e.upgrades || [],
          optionalSpecialRules: e.optionalSpecialRules || [],
          options: e.options || [],
        }));
      setRoster(newRoster);
      setShowReinforcements(
        newRoster.some((e) => {
          const u = findUnit(data, e.unitId);
          return u && u.category === REINFORCEMENT_CATEGORY;
        })
      );
      showBanner("Army list loaded.", false);
    } catch (err) {
      showBanner("Could not load that file: " + err.message, true);
    }
  }, [sources, showBanner]);

  const exportPdf = useCallback(async () => {
    if (!faction || roster.length === 0) {
      showBanner("Add some units to the roster before exporting.", true);
      return;
    }
    const name = window.prompt("Enter a name for this roster:", faction.name || "roster");
    if (name === null) return;
    const fileName = (name.trim() || "roster").replace(/[^\w-]+/g, "-");
    try {
      showBanner("Generating PDF\u2026", false);
      const totals = computeTotals(faction, roster);
      const limit = currentLimit(pointsMode, customLimit);
      const blob = await pdf(
        <RosterPdfDocument faction={faction} roster={roster} limit={limit} totals={totals} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      showBanner("PDF exported.", false);
    } catch (err) {
      showBanner("Could not generate PDF: " + err.message, true);
    }
  }, [faction, roster, pointsMode, customLimit, showBanner]);

  const clearRoster = useCallback(() => {
    if (roster.length === 0) return;
    if (window.confirm("Clear the current roster? This cannot be undone.")) {
      setRoster([]);
    }
  }, [roster]);

  const totals = computeTotals(faction, roster);
  const limit = currentLimit(pointsMode, customLimit);
  const unlockedUnits = computeUnlocked(faction, roster);

  return {
    sources, faction, roster, filterCategory, setFilterCategory, searchTerm, setSearchTerm,
    pointsMode, setPointsMode, customLimit, setCustomLimit, selectedFactionId, banner,
    factionMeta, totals, limit, currentQtyOf, onFactionChange, addUnit, changeQty,
    removeUnit, toggleGroupItem, saveRoster, loadRosterFromFile, exportPdf, moveUnit, clearRoster,
    showReinforcements, toggleReinforcements, unlockedUnits,
  };
}
